import { EmptyValue } from '@/components/common/EmptyValue';
import { Skeleton } from '@/components/ui/skeleton';
import { EMPTY_VALUE } from '@/constants/app.constants';
import { cn } from '@/lib/utils';
import { ACCENT_MARK_CLASS } from '@/styles/accentMark';
import { getInitialsFromName } from '@/utils/getInitialsFromName';
import type { EmployeeInfo } from '../types/attendance.types';

const AVATAR_CLASS = 'size-12 shrink-0 rounded-full sm:size-14';
const META_LABEL_CLASS = 'text-3xs font-medium uppercase tracking-widest text-muted-foreground/60';

interface EmployeeReportHeaderProps {
  employee: EmployeeInfo | undefined;
}

interface MetaFieldProps {
  label: string;
  value: string | null | undefined;
  isLoading: boolean;
}

function MetaField({ label, value, isLoading }: MetaFieldProps) {
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <p className={META_LABEL_CLASS}>{label}</p>
      {isLoading ? (
        <Skeleton className="h-5 w-24 max-w-full" />
      ) : (
        <p className="text-sm font-medium truncate">{value ?? <EmptyValue />}</p>
      )}
    </div>
  );
}

function EmployeeIdentity({ employee }: { employee: EmployeeInfo }) {
  return (
    <>
      <div
        className={cn(
          'flex items-center justify-center text-base font-semibold',
          ACCENT_MARK_CLASS,
          AVATAR_CLASS,
        )}
      >
        {getInitialsFromName(employee.fullName) || EMPTY_VALUE}
      </div>
      <div className="flex-1 min-w-0">
        <p className={META_LABEL_CLASS}>{employee.hrEmpId ?? <EmptyValue />}</p>
        <h2 className="text-base sm:text-xl font-semibold truncate mt-0.5">
          {employee.fullName ?? <EmptyValue />}
        </h2>
        <p className="text-xs sm:text-sm text-muted-foreground truncate">
          {employee.designation ?? <EmptyValue />}
        </p>
      </div>
    </>
  );
}

function EmployeeIdentitySkeleton() {
  return (
    <>
      <Skeleton className={AVATAR_CLASS} />
      <div className="flex flex-1 flex-col gap-1.5 min-w-0">
        <Skeleton className="h-3 w-16" />
        <Skeleton className="h-6 w-48 max-w-full" />
        <Skeleton className="h-4 w-28" />
      </div>
    </>
  );
}

const formatLocation = (employee: EmployeeInfo): string =>
  [employee.thana, employee.district, employee.division].filter(Boolean).join(', ');

export function EmployeeReportHeader({ employee }: EmployeeReportHeaderProps) {
  const metaFields = [
    { label: 'Department', value: employee?.department },
    { label: 'Company', value: employee?.company },
    { label: 'Mobile', value: employee?.mobile },
    { label: 'Contact No', value: employee?.contactNo },
    { label: 'Reporting To', value: employee?.reportingTo },
    { label: 'Address', value: employee?.address },
    { label: 'Location', value: employee ? formatLocation(employee) : undefined },
  ];

  return (
    <div className="rounded-xl border border-border/60 bg-card p-4 sm:p-6">
      <div className="flex items-center gap-3 sm:gap-4 pb-4 sm:pb-5 border-b border-border/50">
        {employee ? <EmployeeIdentity employee={employee} /> : <EmployeeIdentitySkeleton />}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-5 pt-4 sm:pt-5">
        {metaFields.map((field) => (
          <MetaField
            key={field.label}
            label={field.label}
            value={field.value}
            isLoading={employee === undefined}
          />
        ))}
      </div>
    </div>
  );
}
